import { filesApi } from "../../../core/api/files.api";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { Button } from "../../ui/button";
import { useRef, useState, Fragment } from "react";
import { cn } from "../../../shared/utils";
import { getSafeErrorMessage } from "../../../core/api/response-handler";
import { toastOneError, toastOneSuccess } from "../../../toast";
import PropTypes from "prop-types";

export default function SignatureInput({ handleChange, name, value, customStyles, label, disabled }) {
    const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);
    const [seSalveaza, setSeSalveaza] = useState(false);
    const canvasRef = useRef(null);

    const saveSignature = async () => {
        if (isCanvasEmpty || value || seSalveaza) return;
        setSeSalveaza(true);
        try {
            const signatureData = await canvasRef.current.exportImage("png");
            const blob = dataURItoBlob(signatureData);
            const file = new File([blob], "signature.png", { type: blob.type });
            const response = await filesApi.uploadSignature(file);
            // Backend returns plain string URL, not object - handle both formats and trim spaces
            const url = typeof response === 'string' ? response.trim() : response?.url?.trim();
            /**
             * SE-27, al doilea mod de esec — cel care NU arunca. Fara verificarea asta,
             * un raspuns fara URL scria `undefined` in camp si totul parea reusit:
             * niciun mesaj, niciun log, semnatura pe ecran.
             */
            if (!url) {
                toastOneError("Semnătura s-a trimis, dar serverul nu a întors adresa fișierului. Încearcă din nou.");
                return;
            }
            handleChange({ target: { name: name, value: url } });
            toastOneSuccess("Semnătura a fost salvată.");
        } catch (error) {
            /**
             * SE-27: `catch`-ul asta facea DOAR `console.error`. La un esec, nimic nu se
             * schimba pe ecran si parintele credea ca a semnat — exact familia „pare ca
             * ruleaza si nu ruleaza".
             *
             * ⚠️ `filesApi.uploadFile` arata deja un toast propriu la esecul de retea, deci
             * pe calea aia ies doua mesaje. Le las pe amandoua: al lui spune ca fisierul
             * n-a plecat, al meu spune ce inseamna asta pentru semnatura. Alternativa —
             * sa tac aici — reface exact defectul reclamat, si pentru celelalte doua moduri
             * de esec (export esuat, raspuns fara URL) nu exista niciun alt mesaj.
             */
            console.error("Error exporting or uploading signature:", error);
            // `getSafeErrorMessage`, nu `getErrorMessage`: al doilea are incredere in `.message`-ul
            // oricarui `Error`, deci un „Network Error" de la axios ar ajunge textual, in engleza,
            // in fata unui parinte.
            toastOneError(
                getSafeErrorMessage(error, "Semnătura nu a putut fi salvată. Încearcă din nou.")
            );
        } finally {
            setSeSalveaza(false);
        }
    };

    function dataURItoBlob(dataURI) {
        const byteString = atob(dataURI.split(",")[1]);
        const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    }

    const clearSignature = () => {
        canvasRef.current.clearCanvas();
        setIsCanvasEmpty(true);
        handleChange({ target: { name: name, value: "" } });
    };

    const handleStroke = () => {
        setIsCanvasEmpty(false);
    };

    const handleEnd = async () => {
        const currentPaths = await canvasRef.current.exportPaths();
        setIsCanvasEmpty(currentPaths.length === 0);
    };

    return (
        <Fragment>
            <label
                htmlFor={name}
                /* `text-gray-400` (#9ca3af) pe alb da 2,54:1 — masurat, cel mai slab
                   contrast din tot ecranul, pe SINGURA instructiune a unui formular de
                   consimtamant completat de un parinte. `text-dark` (#4f4f4f) da 6,39:1.
                   `font-bold` ramane: bold pe un contrast prost inseamna doar mai multa
                   cerneala palida, dar pe un contrast bun chiar ajuta la citit. */
                className={cn(customStyles, "w-full select-none text-center font-bold text-dark")}>
                {label}
            </label>
            <div className="m-0 h-[300px]">
                <ReactSketchCanvas
                    ref={canvasRef}
                    strokeColor="black"
                    height="100%"
                    strokeWidth={4}
                    canvasColor="white"
                    width="100%"
                    style={{ border: "0px solid #000", borderRadius: "4px" }}
                    className={cn(value || disabled ? "pointer-events-none cursor-not-allowed" : "")}
                    onStroke={handleStroke}
                    onStrokeEnd={handleEnd}
                />
            </div>
            {/**
              * SE-27: UN SINGUR grup de butoane, sub panza.
              *
              * Erau doua butoane cu conditii de randare DIFERITE, suprapuse peste desen
              * (`absolute bottom-1.5 right-0`): utilizatorul vedea cand unul, cand doua, cand
              * niciunul, in acelasi loc — de aici reclamatia. Acum amandoua sunt mereu prezente
              * si se schimba doar `disabled`, deci ce apesi nu se muta sub deget.
              *
              * Scoase si de deasupra panzei: acopereau exact coltul in care se semneaza.
              */}
            <div className="flex flex-wrap justify-end gap-2 py-2">
                <Button
                    type="button"
                    className="min-h-11 bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700 md:min-h-0"
                    disabled={isCanvasEmpty || Boolean(value) || seSalveaza || disabled}
                    onClick={saveSignature}>
                    {seSalveaza ? "Se salvează..." : "Salvare"}
                </Button>
                <Button
                    type="button"
                    variant="destructive"
                    className="min-h-11 px-4 py-2 font-semibold md:min-h-0"
                    disabled={(isCanvasEmpty && !value) || seSalveaza || disabled}
                    onClick={clearSignature}>
                    Șterge semnătura
                </Button>
            </div>
        </Fragment>
    );
}

SignatureInput.propTypes = {
    handleChange: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    customStyles: PropTypes.string,
    disabled: PropTypes.bool,
    value: PropTypes.string,
};
